import { useFormContext } from '@calendly/form'
import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { useSelector } from 'react-redux'

import { BILLING_FEATURE_KEY } from 'components/event_type_edit'
import { isRestrictedFeatureSelector } from 'features/billing'
import { LockedCollectPaymentSection } from 'features/event_type_editor'
import { useEventType } from 'features/event_type_provider'
import { useTrack } from 'hooks/analytics_events'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { paypalConnectedSelector } from 'selectors/integrations/paypal_ppcp'

import { PaymentsEmptyState } from './empty_state'
import { PaymentsEnabledForm } from './payments_enabled_form'
import { RequirePaymentCard } from './require_payment_card'
import {
  headerCss,
  sectionCss,
  tabCss,
} from './styles'

export const PAYMENTS_TAB_KEY = 'payments'

const isPaypalLegacyConnectedOnEventType = (eventType) => !!(
  eventType?.is_paypal_connected || eventType?.isPaypalConnected
)

const isPaypalPpcpConnectedOnEventType = (eventType) => !!(
  eventType?.is_paypal_ppcp_connected || eventType?.isPaypalPpcpConnected
)

const usePaymentConnectionState = () => {
  const eventType = useEventType()
  const { values: { provider } } = useFormContext()
  const { is_stripe_connected: isStripeConnected, payment_method: paymentMethod } = eventType
  const isUpgradeRequired = useSelector((state) => (
    isRestrictedFeatureSelector(state, { featureKey: BILLING_FEATURE_KEY })
  ))
  const isPaypalPpcpConnectedFromStore = useSelector(paypalConnectedSelector)
  const isPaypalConnected = isPaypalLegacyConnectedOnEventType(eventType)
  const isPaypalPpcpConnected = isPaypalPpcpConnectedFromStore
    || isPaypalPpcpConnectedOnEventType(eventType)
  // Mirrors payment.js: empty state when provider is none and nothing is connected.
  // Do not gate on !isUpgradeRequired — otherwise upgrade-needed users with no
  // processor only see a disabled require-payment card and never get connect CTAs.
  const isPaymentProcessorConfigurationRequired = provider === 'none'
    && !isStripeConnected
    && !isPaypalConnected
    && !isPaypalPpcpConnected

  return {
    eventType,
    provider,
    isUpgradeRequired,
    isStripeConnected,
    isPaypalConnected,
    isPaypalPpcpConnected,
    paymentMethodEnabled: paymentMethod?.enabled,
    isPaymentProcessorConfigurationRequired,
  }
}

const getDefaultProvider = ({
  isStripeConnected,
  isPaypalConnected,
  isPaypalPpcpConnected,
}) => {
  if (isStripeConnected) return 'stripe'
  if (isPaypalPpcpConnected) return 'paypal_ppcp'
  if (isPaypalConnected) return 'paypal'
  return 'none'
}

const PaymentsHeader = () => (
  <header className={headerCss}>
    <TitleText level={2}>
      {I18n.t('publisher.event_type_editor_crud.payments.title')}
    </TitleText>
    <BodyText color="muted" size="large">
      {I18n.t('publisher.event_type_editor_crud.payments.subtitle')}
    </BodyText>
  </header>
)

const PaymentsConnectedContent = () => {
  const track = useTrack()
  const { setFieldValue } = useFormContext()
  const {
    eventType,
    provider,
    isUpgradeRequired,
    isStripeConnected,
    isPaypalConnected,
    isPaypalPpcpConnected,
    paymentMethodEnabled,
  } = usePaymentConnectionState()

  const paymentEnabled = provider !== 'none'

  useOnValueChange(paymentEnabled, () => {
    if (!paymentEnabled) {
      setFieldValue('amount', 0, true)
    }
  })

  const handleSwitchToggle = () => {
    if (!paymentEnabled) {
      track({
        name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
        properties: {
          event_type_uuid: eventType.uuid,
          section: 'payments',
          action: 'enable_payment',
        },
      })
      setFieldValue('provider', getDefaultProvider({
        isStripeConnected,
        isPaypalConnected,
        isPaypalPpcpConnected,
      }))
      return
    }

    setFieldValue('provider', 'none')
    track({
      name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
      properties: {
        event_type_uuid: eventType.uuid,
        section: 'payments',
        action: 'disable_payment',
      },
    })
  }

  return (
    <>
      <RequirePaymentCard
        checked={paymentEnabled}
        disabled={isUpgradeRequired}
        onChange={handleSwitchToggle}
      />
      {paymentEnabled && (
        <PaymentsEnabledForm
          provider={provider}
          paymentMethodEnabled={paymentMethodEnabled}
          isUpgradeRequired={isUpgradeRequired}
          isStripeConnected={isStripeConnected}
          isPaypalConnected={isPaypalConnected}
          isPaypalPpcpConnected={isPaypalPpcpConnected}
        />
      )}
    </>
  )
}

export const PaymentsTab = ({ isLocked = false }) => {
  const {
    isPaymentProcessorConfigurationRequired,
    paymentMethodEnabled,
  } = usePaymentConnectionState()

  // Locked check must precede empty state (mirrors payment.js) so connect CTAs
  // are never shown on a locked tab, and disconnect/disable UI remains reachable
  // when payment is already enabled without connected processors.
  let content
  if (isLocked) {
    content = <LockedCollectPaymentSection paymentMethodEnabled={paymentMethodEnabled} />
  } else if (isPaymentProcessorConfigurationRequired) {
    content = <PaymentsEmptyState />
  } else {
    content = <PaymentsConnectedContent />
  }

  return (
    <div className={tabCss} data-testid="payments-tab">
      <PaymentsHeader />
      <div className={sectionCss}>
        {content}
      </div>
    </div>
  )
}
